import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { toast } from "sonner";

import { ButtonLink } from "@/components/button-link";
import { AsyncBoundary, EmptyState } from "@/components/data-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAsync } from "@/hooks/use-async";
import { useAdminKey } from "@/hooks/use-auth";
import type { Maze } from "@/lib/api";
import { ApiError, adminApi } from "@/lib/api";
import { DatasetCard } from "@/routes/admin/mazes/dataset-card";
import { viewBox } from "@/routes/admin/mazes/maze-geometry";
import { FLOOR, Markers, Walls } from "@/routes/admin/mazes/maze-svg";

/**
 * One maze: what it is, who runs in it, and the exploration dataset recorded in it.
 *
 * The maze itself cannot be edited -- a different maze is a different row -- so the only
 * mutable things here are the name, the dataset, and (while nobody uses it) existence.
 */
export function AdminMazeDetailRoute() {
  const { id = "" } = useParams();
  const adminKey = useAdminKey();
  const navigate = useNavigate();
  const maze = useAsync(adminKey ? () => adminApi.maze(id, adminKey) : null, [id, adminKey]);

  if (!adminKey) return <EmptyState title="Sign in as admin" />;

  return (
    <div className="space-y-4">
      <ButtonLink variant="ghost" size="sm" to="/admin/mazes">
        <ArrowLeft className="size-4" aria-hidden />
        Mazes
      </ButtonLink>
      <AsyncBoundary state={maze}>
        {(m) => (
          <div className="space-y-6">
            <Header maze={m} adminKey={adminKey} onChanged={maze.refetch} onDeleted={() => navigate("/admin/mazes")} />
            <InfoCard maze={m} />
            <DatasetCard maze={m} adminKey={adminKey} onChanged={maze.refetch} />
          </div>
        )}
      </AsyncBoundary>
    </div>
  );
}

function Header({
  maze,
  adminKey,
  onChanged,
  onDeleted,
}: {
  maze: Maze;
  adminKey: string;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(maze.name);
  const [busy, setBusy] = useState(false);

  async function rename() {
    setBusy(true);
    try {
      await adminApi.patchMaze(maze.id, { name }, adminKey);
      setRenaming(false);
      onChanged();
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm(`Delete "${maze.name}", its bundle and its dataset? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await adminApi.deleteMaze(maze.id, adminKey);
      toast.success("Maze deleted");
      onDeleted();
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Failed", { duration: 12000 });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {renaming ? (
          <form
            className="flex gap-1.5"
            onSubmit={(e) => {
              e.preventDefault();
              void rename();
            }}
          >
            <Input value={name} onChange={(e) => setName(e.target.value)} className="w-64" autoFocus />
            <Button type="submit" size="sm" disabled={busy}>
              Save
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setRenaming(false)}>
              Cancel
            </Button>
          </form>
        ) : (
          <>
            <h1 className="text-2xl font-semibold tracking-tight">{maze.name}</h1>
            <Button variant="ghost" size="icon-sm" aria-label="Rename" onClick={() => setRenaming(true)}>
              <Pencil className="size-4" aria-hidden />
            </Button>
          </>
        )}
        <Badge variant="secondary" className="font-mono">
          {maze.id}
        </Badge>
      </div>
      <Button
        variant="ghost"
        onClick={remove}
        disabled={busy || maze.used_by.length > 0}
        title={maze.used_by.length ? `In use by ${maze.used_by.map((c) => c.name).join(", ")}` : undefined}
      >
        <Trash2 className="size-4" aria-hidden />
        Delete
      </Button>
    </div>
  );
}

function InfoCard({ maze }: { maze: Maze }) {
  const grid = maze.grid?.cells ? { ...maze.grid, cells: maze.grid.cells } : null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Maze</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-[14rem_1fr]">
        {grid && maze.spawn && maze.target ? (
          <svg viewBox={viewBox(grid)} className="aspect-square w-full rounded-md border" style={{ background: FLOOR }} role="img" aria-label="Maze map with spawn (green) and target (red)">
            <Walls grid={grid} />
            <Markers grid={grid} spawn={maze.spawn.cell} target={maze.target.cell} />
          </svg>
        ) : (
          <div className="bg-muted/40 aspect-square rounded-md border" />
        )}
        <dl className="grid grid-cols-[7rem_1fr] gap-1 text-xs">
          <dt className="text-muted-foreground">Grid</dt>
          <dd className="font-mono">
            {maze.grid?.rows}×{maze.grid?.cols} · {maze.grid?.free_cells} free cells · {maze.panels} panels
          </dd>
          <dt className="text-muted-foreground">Spawn → target</dt>
          <dd className="font-mono">
            ({maze.spawn?.x.toFixed(1)}, {maze.spawn?.y.toFixed(1)}) → ({maze.target?.x.toFixed(1)}, {maze.target?.y.toFixed(1)})
            {maze.target?.reachable ? "" : " · UNREACHABLE"}
            {maze.target ? ` · facing ${maze.target.yaw_deg.toFixed(0)}°` : ""}
          </dd>
          {maze.shortest_path ? (
            <>
              <dt className="text-muted-foreground">Shortest route</dt>
              <dd className="font-mono">
                {maze.shortest_path.metres.toFixed(1)} m · {maze.shortest_path.turn_deg.toFixed(0)}° of turning ·{" "}
                {maze.shortest_path.oracle_steps.toLocaleString()} oracle steps
                <span className="text-muted-foreground font-sans"> — the numerator of every SPL here</span>
              </dd>
            </>
          ) : null}
          <dt className="text-muted-foreground">Camera</dt>
          <dd className="font-mono">
            {maze.camera?.width}×{maze.camera?.height} · fx {maze.camera?.fx.toFixed(1)} fy {maze.camera?.fy.toFixed(1)} · c ({maze.camera?.cx}, {maze.camera?.cy})
          </dd>
          <dt className="text-muted-foreground">Scene</dt>
          <dd className="font-mono break-all">{maze.scene_sha256}</dd>
          <dt className="text-muted-foreground">Source</dt>
          <dd className="font-mono break-all">{maze.source ? JSON.stringify(maze.source) : "—"}</dd>
          <dt className="text-muted-foreground">Used by</dt>
          <dd>
            {maze.used_by.length ? (
              maze.used_by.map((c, i) => (
                <span key={c.id}>
                  {i ? ", " : ""}
                  <Link to={`/admin/challenges/${c.id}/setup`} className="hover:underline">
                    {c.name}
                  </Link>
                </span>
              ))
            ) : (
              <span className="text-muted-foreground">no challenge yet</span>
            )}
          </dd>
        </dl>
      </CardContent>
    </Card>
  );
}
